import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import {
  SERVICE_ACCOUNT_PERMISSIONS,
  serviceAccountFormToPayload,
  serviceAccountToForm,
  validateServiceAccountForm,
} from "../serviceAccountUtils.js";

export function ServiceAccountModal({ open, onClose, onSave, editing = null, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [form, setForm] = useState(() => serviceAccountToForm(null));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  useLayoutEffect(() => {
    if (!open) return;
    setForm(serviceAccountToForm(editing));
    setErrors({});
    setSubmitError(null);
  }, [open, editing]);

  if (!open) return null;

  const handleSubmit = async () => {
    const nextErrors = validateServiceAccountForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      await onSave(serviceAccountFormToPayload(form));
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save service account");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-account-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(440px, 96vw)",
          zIndex: 71,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 14,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit service account" : "Create service account"}
          titleId="service-account-modal-title"
          subtitle={isEdit ? editing.name : "A public and private key pair will be generated automatically"}
          icon={KeyIcon}
          onClose={onClose}
          closeLabel="Close service account form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />
        <div style={{ padding: "16px 20px 20px" }}>
          <label style={{ display: "block", marginBottom: 14 }}>
            <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Name
            </span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Production integration"
              style={{
                width: "100%",
                height: 38,
                padding: "0 12px",
                borderRadius: 8,
                border: `1px solid ${errors.name ? "#EF4444" : c.cardBdr}`,
                background: c.card,
                color: c.text,
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {errors.name && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.name}</span>}
          </label>

          <fieldset style={{ border: "none", margin: "0 0 14px", padding: 0 }}>
            <legend style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em", padding: 0 }}>
              Permissions
            </legend>
            <div
              style={{
                display: "grid",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${errors.permissions ? "#EF4444" : c.cardBdr}`,
                background: c.hover,
              }}
            >
              {SERVICE_ACCOUNT_PERMISSIONS.map(({ key, label }) => (
                <label
                  key={key}
                  style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: c.text, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(form.permissions?.[key])}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        permissions: { ...prev.permissions, [key]: e.target.checked },
                      }))
                    }
                    style={{ accentColor: A, width: 14, height: 14, margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>
            {errors.permissions && (
              <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.permissions}</span>
            )}
          </fieldset>

          {!isEdit && (
            <p style={{ margin: "0 0 14px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
              Public and private keys are generated on create. You will be prompted to copy the private key once.
            </p>
          )}

          {submitError && <p style={{ margin: "0 0 12px", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={saving} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create service account"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21 2-2 2" />
      <path d="m7.5 16.5 9 18" />
      <path d="M3 21v-4l10.5-10.5a2.12 2.12 0 0 1 3 3L6 20z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}
